<template>
    <section v-if="items.length">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader title="Exhibitors" prefix="Event!" :icon="Store" :options="layoutOptions" v-model="mode" />
            
            <!-- Logo wall -->
            <div v-if="mode === 'logos'" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <component :is="e.website ? 'a' : 'div'" v-for="(e, i) in items" :key="i" :href="e.website || undefined"
                    :target="e.website ? '_blank' : undefined" rel="noopener"
                    class="group relative flex flex-col items-center justify-center gap-3 rounded-2xl border border-app bg-card/70 backdrop-blur-2xl p-6 shadow-lg transition-all hover:-translate-y-1 hover:shadow-brand-500/10">
                    <div class="h-16 w-16 rounded-xl overflow-hidden border border-app bg-white flex items-center justify-center">
                        <img v-if="e.logoUrl" :src="e.logoUrl" :alt="e.name" class="h-full w-full object-contain p-1.5" />
                        <span v-else class="text-brand-500 font-black">{{ initials(e.name) }}</span>
                    </div>
                    <div class="text-center min-w-0 w-full">
                        <h3 class="text-header font-bold text-sm truncate group-hover:text-brand-500 transition-colors">
                            {{ e.name }}</h3>
                        <p v-if="e.booth" class="text-body/80 text-[10px] font-bold uppercase tracking-widest mt-1">
                            Booth {{ e.booth }}</p>
                    </div>
                    <ExternalLink v-if="e.website"
                        class="absolute top-3 right-3 h-3.5 w-3.5 text-body/80 group-hover:text-brand-500 transition-colors" />
                </component>
            </div>
    
            <!-- List -->
            <div v-else-if="mode === 'list'"
                class="rounded-2xl border border-app bg-card/70 backdrop-blur-2xl divide-y divide-app">
                <component :is="e.website ? 'a' : 'div'" v-for="(e, i) in items" :key="i" :href="e.website || undefined"
                    :target="e.website ? '_blank' : undefined" rel="noopener"
                    class="group flex items-center gap-4 p-4 sm:p-5">
                    <div class="h-11 w-11 shrink-0 rounded-lg overflow-hidden border border-app bg-white flex items-center justify-center">
                        <img v-if="e.logoUrl" :src="e.logoUrl" :alt="e.name" class="h-full w-full object-contain p-1" />
                        <span v-else class="text-brand-500 font-black text-xs">{{ initials(e.name) }}</span>
                    </div>
                    <div class="min-w-0 flex-1">
                        <h3 class="text-header font-bold leading-snug truncate group-hover:text-brand-500 transition-colors">
                            {{ e.name }}</h3>
                        <p v-if="e.category || e.description" class="text-body/80 text-xs truncate">
                            {{ e.category || e.description }}</p>
                    </div>
                    <span v-if="e.booth"
                        class="hidden sm:inline-flex shrink-0 items-center px-2 py-0.5 rounded-md bg-brand-500/10 text-brand-600 text-[10px] font-bold uppercase tracking-widest">
                        Booth {{ e.booth }}
                    </span>
                    <ExternalLink v-if="e.website"
                        class="h-4 w-4 shrink-0 text-body/80 group-hover:text-brand-500 transition-colors" />
                </component>
            </div>
    
            <!-- Card (default) -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <component :is="e.website ? 'a' : 'div'" v-for="(e, i) in items" :key="i" :href="e.website || undefined"
                    :target="e.website ? '_blank' : undefined" rel="noopener"
                    class="group flex flex-col rounded-2xl border border-app bg-card/70 backdrop-blur-2xl p-5 shadow-lg transition-all hover:-translate-y-1 hover:shadow-brand-500/10">
                    <div class="flex items-center gap-4 mb-3">
                        <div class="h-12 w-12 shrink-0 rounded-xl overflow-hidden border border-app bg-white flex items-center justify-center">
                            <img v-if="e.logoUrl" :src="e.logoUrl" :alt="e.name" class="h-full w-full object-contain p-1" />
                            <span v-else class="text-brand-500 font-black text-sm">{{ initials(e.name) }}</span>
                        </div>
                        <div class="min-w-0">
                            <h3 class="text-header font-bold leading-snug truncate group-hover:text-brand-500 transition-colors">
                                {{ e.name }}</h3>
                            <p v-if="e.category" class="text-body/80 text-xs font-semibold mt-0.5 truncate">{{ e.category }}</p>
                        </div>
                    </div>
                    <p v-if="e.description" class="text-body text-sm line-clamp-2 mb-3">{{ e.description }}</p>
                    <div class="mt-auto flex items-center gap-2">
                        <span v-if="e.booth"
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-500/10 text-brand-600 text-[10px] font-bold uppercase tracking-widest">
                            Booth {{ e.booth }}
                        </span>
                        <ExternalLink v-if="e.website"
                            class="h-3.5 w-3.5 text-body/80 group-hover:text-brand-500 transition-colors" />
                    </div>
                </component>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Store, ExternalLink, LayoutGrid, List, Grid3x3 } from 'lucide-vue-next';
import { EventExhibitor } from '~/types';
import SectionHeader from './SectionHeader.vue';

type ExhibitorLayout = 'card' | 'logos' | 'list';

const props = withDefaults(
    defineProps<{ items: EventExhibitor[]; variant?: ExhibitorLayout }>(),
    { variant: 'card' }
);

const mode = ref<ExhibitorLayout>(props.variant);

const layoutOptions: { value: ExhibitorLayout; label: string; icon: any }[] = [
    { value: 'card', label: 'Cards', icon: LayoutGrid },
    { value: 'logos', label: 'Logo wall', icon: Grid3x3 },
    { value: 'list', label: 'List', icon: List },
];

const initials = (name: string) =>
    (name || '')
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}
</style>
